import React, { useCallback, useState } from 'react';
import { Package, Trash2, Send, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { ScanInput } from '../../../common/scanning/ScanInput';
import { procurementService } from '../../../../services/procurementService';
import { useTranslation } from '../../../../contexts/I18nProvider';
import type {
  Department,
  ProcurementItemLookup,
  ProcurementOrderLineDraft,
} from '../../../../types/procurement';
import { ReplenishmentModal } from './ReplenishmentModal';

interface OrderLinesStepProps {
  legalEntityNo: string;
  legalEntityName: string;
  department: Department;
  operatorName: string;
  lines: ProcurementOrderLineDraft[];
  onLinesChange: (lines: ProcurementOrderLineDraft[]) => void;
  onFinish: () => void;
  onCancel: () => void;
  submitting: boolean;
}

export const OrderLinesStep: React.FC<OrderLinesStepProps> = ({
  legalEntityNo,
  legalEntityName,
  department,
  operatorName,
  lines,
  onLinesChange,
  onFinish,
  onCancel,
  submitting,
}) => {
  const { t } = useTranslation();
  const [scanValue, setScanValue] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [pendingLookup, setPendingLookup] = useState<ProcurementItemLookup | null>(null);

  const appendLine = (line: ProcurementOrderLineDraft) => {
    onLinesChange([...lines, line]);
  };

  const removeLine = (index: number) => {
    onLinesChange(lines.filter((_, i) => i !== index));
  };

  const updateLineQuantity = (index: number, newQty: number) => {
    onLinesChange(
      lines.map((line, i) => (i === index ? { ...line, quantity: newQty } : line))
    );
  };

  const processCode = useCallback(
    async (code: string) => {
      const trimmed = code.trim();
      if (!trimmed) return;

      setLookupError(null);
      setLookupLoading(true);
      try {
        const match = await procurementService.lookupItem(
          legalEntityNo,
          department.departmentNo,
          trimmed
        );

        if (!match) {
          setLookupError(t('warehouse.modules.bestillingssystem.lines.itemNotDefined', { '0': trimmed }, `Vare "${trimmed}" er ikke definert som bestillingsvare for denne avdelingen.`));
          return;
        }

        const recommended = match.recommendedQuantity ?? 0;
        const balance = match.warehouseBalance ?? 0;

        if (balance >= recommended && recommended > 0) {
          // Golden path - auto-add
          appendLine({
            itemNo: match.procurementItemNo,
            description: match.procurementItemDescription,
            quantity: recommended,
            unit: match.recommendedUnit,
          });
          setScanValue('');
        } else {
          // Low balance - show modal
          setPendingLookup(match);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : t('warehouse.modules.bestillingssystem.messages.unknownError', {}, 'Ukjent feil');
        setLookupError(t('warehouse.modules.bestillingssystem.lines.lookupFailed', { '0': message }, `Kunne ikke slå opp vare: ${message}`));
      } finally {
        setLookupLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [legalEntityNo, department.departmentNo, lines, t]
  );

  const handleKeepOriginal = () => {
    if (!pendingLookup) return;
    appendLine({
      itemNo: pendingLookup.procurementItemNo,
      description: pendingLookup.procurementItemDescription,
      quantity: pendingLookup.recommendedQuantity ?? 0,
      unit: pendingLookup.recommendedUnit,
    });
    setPendingLookup(null);
    setScanValue('');
  };

  const handleCancelLine = () => {
    setPendingLookup(null);
    setScanValue('');
  };

  const handleSwitchToReplenishment = () => {
    if (!pendingLookup || !pendingLookup.replenishmentItemNo) return;
    appendLine({
      itemNo: pendingLookup.replenishmentItemNo,
      description: pendingLookup.replenishmentDescription,
      quantity: pendingLookup.recommendedQuantityReplenishmentItem ?? 0,
      unit: pendingLookup.recommendedUnitReplenishmentItem,
    });
    setPendingLookup(null);
    setScanValue('');
  };

  return (
    <div className="space-y-4">
      {/* Compact header summary */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 text-sm flex flex-wrap items-center gap-x-6 gap-y-2">
        <div>
          <span className="text-gray-500">{t('warehouse.modules.bestillingssystem.summary.companyLabel', {}, 'Selskap:')}</span>{' '}
          <strong>
            {legalEntityNo} – {legalEntityName}
          </strong>
        </div>
        <div>
          <span className="text-gray-500">{t('warehouse.modules.bestillingssystem.summary.departmentLabel', {}, 'Avdeling:')}</span>{' '}
          <strong>
            {department.departmentNo} – {department.departmentName}
          </strong>
        </div>
        <div>
          <span className="text-gray-500">{t('warehouse.modules.bestillingssystem.summary.orderedByLabel', {}, 'Bestiller:')}</span> <strong>{operatorName}</strong>
        </div>
      </div>

      {/* Scan area */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Package className="text-blue-600" size={20} />
          <h2 className="text-lg font-semibold text-gray-800">{t('warehouse.modules.bestillingssystem.lines.addLineTitle', {}, 'Legg til varelinje')}</h2>
        </div>
        <ScanInput
          value={scanValue}
          onChange={setScanValue}
          onScan={processCode}
          placeholder={t('warehouse.modules.bestillingssystem.lines.scanPlaceholder', {}, 'Skann eller tast varenummer / GTIN')}
          autoFocus
          loading={lookupLoading}
        />
        <div className="flex items-center gap-2">
          <button
            onClick={() => processCode(scanValue)}
            disabled={lookupLoading || !scanValue.trim()}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium px-4 py-2 rounded flex items-center gap-2"
          >
            {lookupLoading ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />}
            {t('common.buttons.add', {}, 'Legg til')}
          </button>
          {lookupError && (
            <div className="text-sm text-red-700 flex items-center gap-1.5">
              <AlertCircle size={16} />
              {lookupError}
            </div>
          )}
        </div>
      </div>

      {/* Lines table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">
            {t('warehouse.modules.bestillingssystem.lines.tableTitle', {}, 'Bestillingslinjer')}{' '}
            <span className="text-gray-500 font-normal">({lines.length})</span>
          </h3>
        </div>
        {lines.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">
            {t('warehouse.modules.bestillingssystem.lines.emptyState', {}, 'Ingen linjer lagt til ennå. Skann eller tast et varenummer over.')}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-700 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-2 text-left">#</th>
                <th className="px-4 py-2 text-left">{t('warehouse.general.itemNumber', {}, 'Varenummer')}</th>
                <th className="px-4 py-2 text-left">{t('common.fields.description', {}, 'Beskrivelse')}</th>
                <th className="px-4 py-2 text-right">{t('common.fields.quantity', {}, 'Antall')}</th>
                <th className="px-4 py-2 text-left">{t('warehouse.modules.bestillingssystem.lines.unit', {}, 'Enhet')}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={`${l.itemNo}-${i}`} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-500">{i + 1}</td>
                  <td className="px-4 py-2 font-medium">{l.itemNo}</td>
                  <td className="px-4 py-2 text-gray-700">{l.description || '—'}</td>
                  <td className="px-4 py-2 text-right">
                    <input
                      type="number"
                      step="any"
                      min={0}
                      value={l.quantity}
                      onChange={(e) => {
                        const v = e.target.value === '' ? 0 : Number(e.target.value);
                        if (!Number.isNaN(v)) updateLineQuantity(i, v);
                      }}
                      onFocus={(e) => e.target.select()}
                      className="w-24 text-right border border-gray-300 rounded px-2 py-1 font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      aria-label={t('common.fields.quantity', {}, 'Antall')}
                    />
                  </td>
                  <td className="px-4 py-2 text-gray-700">{l.unit || '—'}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => removeLine(i)}
                      className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                      aria-label={t('warehouse.modules.bestillingssystem.lines.removeLine', {}, 'Fjern linje')}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer actions */}
      <div className="flex items-center justify-between gap-3">
        <button
          onClick={onCancel}
          disabled={submitting}
          className="text-gray-700 hover:text-gray-900 px-4 py-2 font-medium"
        >
          {t('warehouse.modules.bestillingssystem.lines.cancelOrder', {}, 'Avbryt hele bestillingen')}
        </button>
        <button
          onClick={onFinish}
          disabled={lines.length === 0 || submitting}
          className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-lg flex items-center gap-2 shadow"
        >
          {submitting ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
          {t('warehouse.modules.bestillingssystem.lines.finishOrder', { '0': lines.length }, `Avslutt bestilling (${lines.length})`)}
        </button>
      </div>

      {pendingLookup && (
        <ReplenishmentModal
          lookup={pendingLookup}
          onKeepOriginal={handleKeepOriginal}
          onCancelLine={handleCancelLine}
          onSwitchToReplenishment={handleSwitchToReplenishment}
          onClose={handleCancelLine}
        />
      )}
    </div>
  );
};
